import './FieldTable.scss';

import { AccessLoadingSpinner } from '../AccessLoadingSpinner';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { DataAccessSearch } from '../DataAccessSearch';
import { getColumns } from './FieldColumns';
import { SelectionTable } from '@ui/tables';
import { SonarCard } from '@ui/general';
import { sort } from '../@arch/lib';
import { useAddPermissionObject } from '@hooks/data-security';

import {
	AccessExplainerField,
	AccessExplainerObjectDetails,
	AccessObject,
	AccessType,
	DataSensitivityLevelId,
	SchemaFormat
} from '@sonar-software/sonar-core';
import { Button, Space } from 'antd';
import { ConvertToDynamicQueryLanguage, TableFilter, TableModifier, TableQueryParams } from '@ui/table-modifier';
import { DynamicFilter, SonarSortDirection, SonarV3SortDirection, SortByOption } from '@sonar-software/sonar-ts-types';
import { Else, If, Then, When } from 'react-if';
import React, { useMemo } from 'react';
import {
	translateFilterAndOrderingToQueryParams,
	translateFilterDisplayNamesToQueryParams,
	translateFilterQueryParamsToDynamicQueryLanguage,
	translateOrderQueryParamsToDynamicQueryLanguage
} from '@ui/table-modifier/@arch/lib';
import { UpdateQueryParams, useSelectionTableWithInversion } from '@hooks';

export interface FieldTableProps {
	convertToDql: ConvertToDynamicQueryLanguage;
	data?: AccessExplainerObjectDetails | null;
	defaultAccessSearchName?: string;
	defaultObjectSearchName?: string;
	displayNameString?: string;
	exportButton?: React.ReactNode;
	filterString?: string;
	highlightDifferences?: boolean;
	isLoading?: boolean;
	isRefreshing?: boolean;
	isFiltering?: boolean;
	notificationBar?: React.ReactNode;
	onAccessChange?: (accessName: string, type: AccessType, value: string) => void;
	onAdditionalColumnsChange?: (columns: Array<AccessObject>) => void;
	onForceRefresh: () => void;
	onObjectChange?: (accessName: string, type: AccessType, value: string) => void;
	onRemoveColumn?: (accessKey: string) => void;
	onRowConfig?: (record: AccessExplainerField) => React.HTMLAttributes<HTMLTableRowElement>;
	orderString?: string;
	showAddPermissionButton?: boolean;
	showRemoveColumn?: boolean;
	title: string;
	updateParams: UpdateQueryParams<TableQueryParams>;
}

const SENSITIVITY = 'Sensitivity';

const sortOptions: Array<SortByOption> = [
	{
		direction: SonarV3SortDirection.Ascending,
		key: 'FieldName',
		label: COPY_SPECIFIC.FIELD_NAME
	},
	{
		direction: SonarV3SortDirection.Ascending,
		key: SENSITIVITY,
		label: COPY_SPECIFIC.SENSITIVITY
	}
];

const filters: Array<TableFilter> = [
	{
		filterFormat: SchemaFormat.BooleanToggleInverse,
		filterGroup: COPY_SPECIFIC.DATA_ACCESS,
		filterId: 'IncludeIdentical',
		filterLabel: COPY_SPECIFIC.INCLUDE_FIELDS_WITHOUT_DIFFERENCES
	}, {
		filterFormat: SchemaFormat.Enum,
		filterGroup: COPY_SPECIFIC.DATA_ACCESS,
		filterId: 'AccessLevel',
		filterLabel: COPY_SPECIFIC.FIELD_ACCESS
	}, {
		filterFormat: SchemaFormat.Primary,
		filterGroup: COPY_SPECIFIC.FIELDS,
		filterId: 'ObjectLabel',
		filterLabel: COPY_SPECIFIC.FIELD_NAME,
		filterMetadata: 'ObjectName'
	}, {
		filterFormat: SchemaFormat.PrimaryInternal,
		filterGroup: COPY_SPECIFIC.FIELDS,
		filterId: 'ObjectName',
		filterLabel: COPY_SPECIFIC.API_NAME
	}, {
		filterFormat: SchemaFormat.Enum,
		filterGroup: COPY_SPECIFIC.FIELDS,
		filterId: 'Type',
		filterLabel: COPY_SPECIFIC.FIELD_TYPE
	}, {
		filterFormat: SchemaFormat.Enum,
		filterGroup: COPY_SPECIFIC.FIELDS,
		filterId: 'ComplianceCategory',
		filterLabel: COPY_SPECIFIC.COMPLIANCE_CATEGORY
	}, {
		filterFormat: SchemaFormat.Enum,
		filterGroup: COPY_SPECIFIC.FIELDS,
		filterId: 'FieldUsageId',
		filterLabel: COPY_SPECIFIC.FIELD_USAGE
	}, {
		filterFormat: SchemaFormat.Enum,
		filterGroup: COPY_SPECIFIC.FIELDS,
		filterId: 'DataSensitivityLevelId',
		filterLabel: COPY_SPECIFIC.DATA_SENSITIVITY
	}, {
		filterFormat: SchemaFormat.Tags,
		filterGroup: COPY_SPECIFIC.FIELDS,
		filterId: 'TagIds',
		filterLabel: COPY_SPECIFIC.TAG
	}, {
		filterFormat: SchemaFormat.LookAheadSalesforceUser,
		filterGroup: COPY_SPECIFIC.FIELDS,
		filterId: 'CreatedBy',
		filterLabel: COPY_SPECIFIC.CREATED_BY
	}, {
		filterFormat: SchemaFormat.DateTime,
		filterGroup: COPY_SPECIFIC.FIELDS,
		filterId: 'CreatedDate',
		filterLabel: COPY_SPECIFIC.CREATED_DATE
	}, {
		filterFormat: SchemaFormat.LookAheadSalesforceUser,
		filterGroup: COPY_SPECIFIC.FIELDS,
		filterId: 'LastModifiedBy',
		filterLabel: COPY_SPECIFIC.LAST_MODIFIED_BY
	}, {
		filterFormat: SchemaFormat.DateTime,
		filterGroup: COPY_SPECIFIC.FIELDS,
		filterId: 'LastModifiedDate',
		filterLabel: COPY_SPECIFIC.LAST_MODIFIED_DATE
	}
];

export function convertAccessFieldToDql(
	sortBy: string | undefined,
	sortDirection: SonarSortDirection | undefined,
	selectedFilters: DynamicFilter
) {
	const selectedSortOption =
		sortOptions
			.find((option) => option.key === (sortBy ?? sortOptions[ 0 ].key)) || sortOptions[ 0 ];

	return {
		displays: translateFilterDisplayNamesToQueryParams(selectedFilters),
		filters: translateFilterQueryParamsToDynamicQueryLanguage(selectedFilters),
		ordering: translateOrderQueryParamsToDynamicQueryLanguage({
			sortBy: selectedSortOption.key,
			sortDirection: sortDirection ?? selectedSortOption.direction
		})
	};
}

const getRowKey = (field: AccessExplainerField) => field.fieldId;

export const FieldTable: React.FC<FieldTableProps> = ({
	convertToDql,
	data,
	defaultAccessSearchName,
	defaultObjectSearchName,
	displayNameString,
	exportButton,
	filterString,
	highlightDifferences = false,
	isLoading = false,
	isRefreshing = false,
	isFiltering = false,
	notificationBar,
	onAccessChange,
	onAdditionalColumnsChange,
	onForceRefresh,
	onObjectChange,
	onRemoveColumn,
	onRowConfig,
	orderString,
	showAddPermissionButton = false,
	showRemoveColumn = false,
	title,
	updateParams
}) => {
	const [ sorting, setSorting ] = React.useState<boolean>(false);

	const {
		combinedAccessObjects,
		additionalColumnIds,
		addPermissionColumn,
		handleForceRefresh,
		removePermissionColumn
	} = useAddPermissionObject({
		data,
		isLoading,
		onAdditionalColumnsChange,
		onForceRefresh
	});

	const columns = useMemo(() => {
		if (!data) {
			return [];
		}

		const handleRemoveColumn = (accessKey: string) => {
			// Try to remove from additional columns first
			const wasRemovedFromAdditional = removePermissionColumn(accessKey);

			// If not found in additional columns, delegate to parent's remove handler
			if (!wasRemovedFromAdditional && onRemoveColumn) {
				onRemoveColumn(accessKey);
			}
		};

		return getColumns(
			combinedAccessObjects,
			highlightDifferences,
			additionalColumnIds,
			showAddPermissionButton ? addPermissionColumn : undefined,
			showRemoveColumn ? handleRemoveColumn : undefined
		);
	}, [
		data,
		combinedAccessObjects,
		additionalColumnIds,
		highlightDifferences,
		addPermissionColumn,
		removePermissionColumn,
		onRemoveColumn,
		showAddPermissionButton,
		showRemoveColumn
	]);

	const sortedData = useMemo(() => {
		if (!data) {
			return [];
		}

		setSorting(true);

		const params = translateFilterAndOrderingToQueryParams(
			filters, sortOptions, filterString, orderString
		);

		const sorted = [ ...data.fields ].sort((a, b) => {
			if (params.sortBy === SENSITIVITY) {
				return sort(
					params.sortDirection,
					DataSensitivityLevelId[ a.dataSensitivityLevelId ],
					DataSensitivityLevelId[ b.dataSensitivityLevelId ],
					a.fieldName,
					b.fieldName
				);
			}

			return sort(
				params.sortDirection, a.fieldName, b.fieldName
			);
		});

		setSorting(false);

		return sorted;

	}, [ data, filterString, orderString ]);

	const selectionData = useSelectionTableWithInversion<AccessExplainerField, string>({
		getRowKey,
		results: sortedData,
		totalCount: sortedData.length
	});

	const showSkeleton = isLoading && (!data || !isRefreshing) && !isFiltering;

	const showTransparentOverlay =
		(isLoading && Boolean(data) && (isRefreshing || isFiltering)) ||
		sorting;

	return (
		<SonarCard title={ title }>
			<Space>
				<When condition={ Boolean(defaultAccessSearchName) || Boolean(onAccessChange) }>
					<DataAccessSearch
						allowClear={ false }
						defaultValue={ defaultAccessSearchName }
						onSelect={ onAccessChange }
						showCompare={ false } />
				</When>
				<When
					condition={
						(Boolean(defaultObjectSearchName) && Boolean(defaultAccessSearchName)) ||
                        (Boolean(onObjectChange) && Boolean(onAccessChange))
					}>
					{ COPY_SPECIFIC.AND }
				</When>
				<When condition={ Boolean(defaultObjectSearchName) || Boolean(onObjectChange) }>
					<DataAccessSearch
						allowClear={ false }
						defaultValue={ defaultObjectSearchName }
						onSelect={ onObjectChange }
						placeholder={ COPY_SPECIFIC.SEARCH_FOR_OBJECTS }
						showCompare={ false }
						specificType={ AccessType.Object } />
				</When>
			</Space>
			<If condition={ showSkeleton }>
				<Then>
					<AccessLoadingSpinner
						loading={ isLoading }
						sorting={ sorting } />
				</Then>
				<Else>
					<TableModifier
						convertToDynamicQueryLanguage={ convertToDql }
						deferFiltering
						displayNameString={ displayNameString }
						extra={
							<>
								<Button
									loading={ isLoading }
									onClick={ handleForceRefresh }>
									{ COPY_SPECIFIC.REFRESH_DATA }
								</Button>
								{ exportButton }
							</>
						}
						filterDisplayType='menu'
						filters={ filters }
						filtersString={ filterString }
						onChange={ updateParams }
						orderString={ orderString }
						sortOptions={ sortOptions } />
					{ notificationBar }
					<AccessLoadingSpinner
						loading={ showTransparentOverlay }
						sorting={ sorting }>
						<SelectionTable
							className='data-access-field-table'
							columns={ columns }
							dataSource={ sortedData }
							forceShowSelection={ false }
							hideSelectAll
							hideSelection
							onRow={ onRowConfig }
							pagination={ { defaultPageSize: 50 } }
							rowClassName={ onRowConfig ? 'clickable' : undefined }
							rowKey={ getRowKey }
							scroll={ {
								scrollToFirstRowOnChange: true,
								x: 'max-content'
							} }
							selectionData={ selectionData } />
					</AccessLoadingSpinner>
				</Else>
			</If>
		</SonarCard>
	);
};
